import axios, { AxiosResponse } from "axios";

import { getError } from "../../utils/getError";
import { BuildSendMailParams, ContentTypeValue, MailConfiguration, MESSAGES_SENDMAIL, Recipients, RecipientsMap, RecipientsType, SendMailRequest } from "../../interfaces/services/email-services";
import { displayErrorMessage, getUserInfo } from "../../utils";
import CustomHttpClient from "../customHttpClient";
import localStorageHelper from "../../utils/localStorageHelper";

const { REACT_APP_LAMBDA_MAIL_URL } = process.env;

const customHttpClient = CustomHttpClient({ baseUrl: 'productores' });

export async function replaceParametersSubject(text: string, ...params: readonly unknown[]) {
  return text.replace(/\{(\d+)\}/g, (_, indexStr) => {
    const index = Number(indexStr);
    if (Number.isNaN(index) || index < 0 || index >= params.length) {
      return `{${indexStr}}`;
    }
    return String(params[index]);
  });
}

export async function buildSendMailRequest(params: BuildSendMailParams, distributionListKey: string, additionalRecipients?: Recipients[]): Promise<SendMailRequest | null> {
  const {
    subject,
    body,
    contentType = "HTML",
    includeAttachments = true,
    attachmentBase64,
    attachmentName = "",
    attachmentMimeType = ContentTypeValue.OCTET_STREAM,
    saveToSentItems = false,
  } = params;

  const distributionListResponse = await EmailService.getDistributionList(distributionListKey);
  
  function mapDestinatarios(config: MailConfiguration, additionalRecipients?: Recipients[]): RecipientsMap {
    const result: { to: string[]; cc: string[]; cco: string[] } = {
      to: [],
      cc: [],
      cco: []
    };

    const allDestinatarios = [
      ...(config.destinatarios || []),
      ...(additionalRecipients ?? [])
    ];
  
    if (!allDestinatarios.length) {
      return {
        to: "",
        cc: "",
        cco: "",
      };
    }

    allDestinatarios.forEach(({ toAddress, toType }) => {
      if (toType === RecipientsType.NORMAL) result.to.push(toAddress);
      else if (toType === RecipientsType.CC) result.cc.push(toAddress);
      else if (toType === RecipientsType.CCO) result.cco.push(toAddress);
    });

    return {
      to: result.to.join(","),
      cc: result.cc.join(","),
      cco: result.cco.join(","),
    };
  }

  const { to, cc, cco } = mapDestinatarios(distributionListResponse, additionalRecipients);
  
  const toRecipients = to.split(",").filter(email => email.trim() !== "").map(email => ({
    emailAddress: { address: email.trim() }
  }));
  
  const ccRecipients = cc?.split(",").filter(email => email.trim() !== "").map(email => ({
    emailAddress: { address: email.trim() }
  })) ?? [];
  
  const bccRecipients = cco?.split(",").filter(email => email.trim() !== "").map(email => ({
    emailAddress: { address: email.trim() }
  })) ?? [];
  
  const hasRecipients = toRecipients.length > 0 || ccRecipients.length > 0 || bccRecipients.length > 0;
  if (!hasRecipients) {
    displayErrorMessage(`¡Envío de correo electrónico omitido! No se encontraron destinatarios válidos en lista de distribución ${distributionListKey}`);
    return null;
  }

  const attachments =
    includeAttachments && attachmentBase64
      ? [
          {
            "@odata.type": "#microsoft.graph.fileAttachment" as const,
            name: attachmentName,
            contentType: attachmentMimeType,
            contentBytes: attachmentBase64,
          },
        ]
      : [];

  return {
    message: {
      subject: subject ?? distributionListResponse.subject,
      body: {
        contentType,
        content: body ?? '',
      },
      toRecipients,
      ...(ccRecipients.length && { ccRecipients }),
      ...(bccRecipients.length && { bccRecipients }),
      ...(attachments.length && { attachments }),
    },
    ...(saveToSentItems !== undefined && { saveToSentItems }),
  };
}

export const EmailService = {

  async getDistributionList(distributionListKey: string): Promise<MailConfiguration> {
    const url = 'listaDistribuciones';
    const params = {
      ...getUserInfo(),
      listaDistribucion: distributionListKey,
    };

    try {
      const response = await customHttpClient.get(url, { params });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
    async sendMailViaLambda(payload: SendMailRequest): Promise<AxiosResponse<boolean>> {
          const url = REACT_APP_LAMBDA_MAIL_URL;
          const token = localStorageHelper.get<string>("access_token");
          try {
            const response = await axios.post(url!, payload, {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
            });
            if (response.status === 200) {
              return response;
            } else {
              displayErrorMessage(MESSAGES_SENDMAIL.ERROR_MESSAGE);
              return response;
            }
          } catch (error: any) {
            displayErrorMessage(MESSAGES_SENDMAIL.ERROR_MESSAGE);            
            throw new Error(getError(error));
          }
      }
}

export default EmailService;